import { useMemo } from 'react';

import { formatterPercentage } from '@/utilities/format';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';

import type { PortfolioDetailedReturns } from '../../../../hooks/usePortfolioDetailedReturns';

export const BarChart = ({
  value,
  maxValue,
}: {
  value: number;
  maxValue: number;
}) => {
  const { isPositive, barStyle } = useMemo(() => {
    const width = Math.min(Math.abs((value / maxValue) * 50), 50);
    const isPositive = value >= 0;

    return {
      isPositive,
      barStyle: {
        width: `${width}%`,
        transform: `translateX(${isPositive ? '0%' : '-100%'})`,
      },
    };
  }, [value, maxValue]);

  return (
    <div className="relative h-3.5 w-full overflow-hidden rounded-sm bg-transparent-subtle">
      <div className="absolute left-1/2 z-1 h-full w-0.5 -translate-x-1/2 bg-p-white-130" />
      <div
        data-testid="bar-chart"
        className={`absolute left-1/2 h-full -translate-x-1/2 ${
          isPositive ? 'rounded-r-sm bg-good' : 'rounded-l-sm bg-bad'
        }`}
        style={barStyle}
      />
    </div>
  );
};

type ContributorChartProps = {
  holding: PortfolioDetailedReturns['holdings'][0];
  highestGain: number;
  currencyISO: string;
};

export const ContributorChart = ({
  holding,
  highestGain,
  currencyISO,
}: ContributorChartProps) => (
  <div className="grid grid-cols-[minmax(50px,auto)_1fr_minmax(75px,auto)] items-center justify-between gap-x2">
    <div className="flex flex-col">
      <strong className="max-w-[50px] truncate text-xs text-solid">
        {holding.symbol}
      </strong>
      <span className="max-w-[50px] truncate text-tiny text-softer">
        {holding.company.name}
      </span>
    </div>
    <BarChart
      value={holding.detailedReturns.totalGains}
      maxValue={highestGain}
    />
    <div className="flex flex-col">
      <strong className="text-right text-xs text-solid">
        {formatCurrencyToScaleTruncated(
          holding.detailedReturns.totalGains,
          currencyISO
        )}
      </strong>
      <span className="text-right text-tiny text-softer">
        <PositiveNegativeValue
          value={holding.detailedReturns.totalGainsPercent}
        >
          {formatterPercentage(holding.detailedReturns.totalGainsPercent)}
        </PositiveNegativeValue>
      </span>
    </div>
  </div>
);
